/**
 * 🔍 ADVANCED DATA COLLECTORS
 *
 * Implementasi praktis untuk mengumpulkan semua data metrics
 * yang telah didefinisikan dalam comprehensiveDataStructure.js
 *
 * @version 1.0.0
 * @created 2025-08-18
 */

import {
  BrowserHardwareMetrics,
  PerformanceMetrics,
  ContentMetrics
} from './comprehensiveDataStructure.js'

// ============================================================================
// 1. 🌐 NETWORK PERFORMANCE COLLECTOR
// ============================================================================

export class NetworkPerformanceCollector {
  constructor() {
    this.speedTestInProgress = false
    this.connectionHistory = []
    this.requestMetrics = new Map()
  }

  /**
   * Comprehensive network performance test
   */
  async collectNetworkMetrics() {
    const metrics = {}

    try {
      // Basic connection info
      const connection =
        navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if (connection) {
        metrics.connection_type = connection.type || 'unknown'
        metrics.effective_type = connection.effectiveType || 'unknown'
        metrics.downlink_speed = connection.downlink || null
        metrics.rtt = connection.rtt || null
      }

      // Speed test (non-blocking)
      this.performSpeedTest(metrics)

      // DNS resolution test
      await this.testDNSResolution(metrics)

      // Connection stability
      this.collectConnectionStability(metrics)

      // Request performance analysis
      this.analyzeRequestPerformance(metrics)

      metrics.timestamp = new Date().toISOString()
      return metrics
    } catch (error) {
      console.error('Network metrics collection failed:', error)
      return null
    }
  }

  async performSpeedTest(metrics) {
    if (this.speedTestInProgress) {
      return
    }

    this.speedTestInProgress = true
    const testFileUrl = '/assets/filler.mp4' // Use existing test file
    const startTime = performance.now()

    try {
      const response = await fetch(testFileUrl + '?t=' + Date.now(), {
        cache: 'no-cache'
      })

      const endTime = performance.now()
      const contentLength = response.headers.get('content-length')

      if (contentLength) {
        const bytes = parseInt(contentLength, 10)
        const duration = (endTime - startTime) / 1000 // seconds
        const speedMbps = (bytes * 8) / (duration * 1000000) // Convert to Mbps

        metrics.download_test = {
          start_time: startTime,
          end_time: endTime,
          bytes_downloaded: bytes,
          calculated_speed_mbps: Math.round(speedMbps * 100) / 100,
          test_file_url: testFileUrl,
          success: true
        }
      }
    } catch (error) {
      metrics.download_test.success = false
      console.warn('Speed test failed:', error)
    } finally {
      this.speedTestInProgress = false
    }
  }

  async testDNSResolution(metrics) {
    const testDomain = window.location.host
    const startTime = performance.now()

    try {
      // Use a small image request to test DNS + first byte
      const response = await fetch(`//${testDomain}/assets/images/favicon.ico?t=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-cache'
      })

      const endTime = performance.now()
      metrics.dns_resolution_time = Math.round(endTime - startTime)
    } catch (error) {
      console.warn('DNS test failed:', error)
    }
  }

  collectConnectionStability(metrics) {
    // Count connection change events from history
    metrics.connection_changes = this.connectionHistory.length

    // Calculate offline duration
    let totalOffline = 0
    this.connectionHistory.forEach((event) => {
      if (event.type === 'offline' && event.duration) {
        totalOffline += event.duration
      }
    })
    metrics.total_offline_duration = totalOffline
  }

  analyzeRequestPerformance(metrics) {
    // Analyze slow and failed requests from our tracking
    metrics.failed_requests = Array.from(this.requestMetrics.values())
      .filter((req) => req.failed)
      .slice(-10) // Last 10 failed requests

    metrics.slow_requests = Array.from(this.requestMetrics.values())
      .filter((req) => req.duration > 5000) // Slower than 5 seconds
      .slice(-10)
  }

  // Event listeners for connection monitoring
  startConnectionMonitoring() {
    window.addEventListener('online', () => {
      this.connectionHistory.push({
        type: 'online',
        timestamp: Date.now()
      })
    })

    window.addEventListener('offline', () => {
      this.connectionHistory.push({
        type: 'offline',
        timestamp: Date.now()
      })
    })
  }
}

// ============================================================================
// 2. 🖥️ BROWSER & HARDWARE COLLECTOR
// ============================================================================

export class BrowserHardwareCollector {
  async collectBrowserHardwareMetrics() {
    const metrics = { ...BrowserHardwareMetrics }

    try {
      // Browser information
      this.collectBrowserInfo(metrics)

      // Device information
      this.collectDeviceInfo(metrics)

      // Hardware capabilities
      await this.collectHardwareInfo(metrics)

      // Feature detection
      this.detectFeatures(metrics)

      metrics.timestamp = new Date().toISOString()
      return metrics
    } catch (error) {
      console.error('Browser/Hardware metrics collection failed:', error)
      return null
    }
  }

  collectBrowserInfo(metrics) {
    const ua = navigator.userAgent

    // Enhanced browser detection
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      metrics.browser.name = 'chrome'
      metrics.browser.engine = 'blink'
      const match = ua.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/)
      metrics.browser.full_version = match ? match[1] : 'unknown'
      metrics.browser.version = match ? match[1].split('.')[0] : 'unknown'
    } else if (ua.includes('Firefox')) {
      metrics.browser.name = 'firefox'
      metrics.browser.engine = 'gecko'
      const match = ua.match(/Firefox\/(\d+\.\d+)/)
      metrics.browser.full_version = match ? match[1] : 'unknown'
      metrics.browser.version = match ? match[1].split('.')[0] : 'unknown'
    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      metrics.browser.name = 'safari'
      metrics.browser.engine = 'webkit'
      const match = ua.match(/Version\/(\d+\.\d+)/)
      metrics.browser.full_version = match ? match[1] : 'unknown'
      metrics.browser.version = match ? match[1].split('.')[0] : 'unknown'
    }

    // Additional browser info
    metrics.browser.user_agent = ua
    metrics.browser.language = navigator.language
    metrics.browser.languages = navigator.languages || []

    // Storage capabilities
    metrics.browser.cookies_enabled = navigator.cookieEnabled
    metrics.browser.local_storage_enabled = this.testLocalStorage()
    metrics.browser.session_storage_enabled = this.testSessionStorage()
    metrics.browser.indexed_db_enabled = 'indexedDB' in window
  }

  collectDeviceInfo(metrics) {
    const ua = navigator.userAgent

    // Device type detection
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      if (/iPad|Android(?=.*Tablet)|Tablet/i.test(ua)) {
        metrics.device.type = 'tablet'
      } else {
        metrics.device.type = 'mobile'
      }
    } else if (ua.includes('TV') || ua.includes('SmartTV')) {
      metrics.device.type = 'tv'
    } else if (ua.includes('Kiosk') || ua.includes('BroadSign') || ua.includes('Scala')) {
      metrics.device.type = 'kiosk'
    } else {
      metrics.device.type = 'desktop'
    }

    // OS detection
    if (ua.includes('Windows')) {
      metrics.device.os = 'windows'
    } else if (ua.includes('Mac OS')) {
      metrics.device.os = 'macos'
    } else if (ua.includes('Android')) {
      metrics.device.os = 'android'
    } else if (ua.includes('iPhone') || ua.includes('iPad')) {
      metrics.device.os = 'ios'
    } else if (ua.includes('Linux')) {
      metrics.device.os = 'linux'
    }

    // Touch capabilities
    metrics.device.touch_support = 'ontouchstart' in window
    metrics.device.max_touch_points = navigator.maxTouchPoints || 0

    // 🕒 LOCAL TIME & TIMEZONE COLLECTION
    this.collectLocalTimeInfo(metrics)

    // Screen information
    const screen = window.screen
    const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
    metrics.device.screen = {
      width: screen.width,
      height: screen.height,
      available_width: screen.availWidth,
      available_height: screen.availHeight,
      color_depth: screen.colorDepth,
      pixel_ratio: window.devicePixelRatio || 1,
      orientation: screen.orientation ? screen.orientation.type : orientation
    }

    // Viewport
    metrics.device.viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      zoom_level: Math.round((window.outerWidth / window.innerWidth) * 100) / 100
    }
  }

  /**
   * 🕒 Comprehensive Local Time Information Collection
   */
  collectLocalTimeInfo(metrics) {
    const now = new Date()
    const performanceNow = performance.now()
    const dateNow = Date.now()

    // Basic time information
    metrics.device.local_time = {
      // Current timestamps
      current_local_time: now.toISOString(),
      current_utc_time: new Date().toISOString(),

      // Timezone information
      timezone_offset: now.getTimezoneOffset(), // Minutes from UTC
      timezone_offset_hours: Math.round((now.getTimezoneOffset() / -60) * 100) / 100,

      // Performance timestamps
      performance_now: performanceNow,
      date_now: dateNow,
      high_res_timestamp: this.getHighResTimestamp()
    }

    // Enhanced timezone detection
    try {
      // Get IANA timezone identifier
      if (Intl && Intl.DateTimeFormat) {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
        metrics.device.local_time.timezone = timeZone

        // Get timezone display name
        try {
          const tzNames = new Intl.DisplayNames(['en'], { type: 'region' })
          const tzLongName = new Intl.DateTimeFormat('en', {
            timeZone: timeZone,
            timeZoneName: 'long'
          })
            .formatToParts(now)
            .find((part) => part.type === 'timeZoneName')

          const tzShortName = new Intl.DateTimeFormat('en', {
            timeZone: timeZone,
            timeZoneName: 'short'
          })
            .formatToParts(now)
            .find((part) => part.type === 'timeZoneName')

          metrics.device.local_time.timezone_name = tzLongName ? tzLongName.value : null
          metrics.device.local_time.timezone_abbreviation = tzShortName ? tzShortName.value : null
        } catch (error) {
          console.warn('Timezone name detection failed:', error)
        }

        // Detect locale and format preferences
        const locale = Intl.DateTimeFormat().resolvedOptions().locale
        metrics.device.local_time.locale = locale

        // Get date/time format examples
        try {
          const dateFormatter = new Intl.DateTimeFormat(locale)
          const timeFormatter = new Intl.DateTimeFormat(locale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: undefined // Let browser decide
          })

          metrics.device.local_time.date_format = dateFormatter.format(now)
          metrics.device.local_time.time_format = timeFormatter.format(now)

          // Detect 12h/24h format
          const hour12Test = new Intl.DateTimeFormat(locale, {
            hour: 'numeric',
            hour12: false
          }).format(now)
          const hour24Test = new Intl.DateTimeFormat(locale, {
            hour: 'numeric',
            hour12: true
          }).format(now)

          metrics.device.local_time.uses_24h_format =
            !hour24Test.includes('AM') && !hour24Test.includes('PM')
        } catch (error) {
          console.warn('Date/time format detection failed:', error)
        }

        // Detect first day of week (locale-dependent)
        try {
          const weekInfo = new Intl.Locale(locale).weekInfo
          metrics.device.local_time.first_day_of_week = weekInfo ? weekInfo.firstDay : null
        } catch (error) {
          // Fallback method for older browsers
          const sundayDate = new Date(2023, 0, 1) // A known Sunday
          const mondayDate = new Date(2023, 0, 2) // A known Monday

          const sundayWeekday = sundayDate.toLocaleDateString(locale, { weekday: 'short' })
          const mondayWeekday = mondayDate.toLocaleDateString(locale, { weekday: 'short' })

          // This is a rough heuristic
          metrics.device.local_time.first_day_of_week = 1 // Default to Monday
        }

        // Calendar system detection
        try {
          const calendar = new Intl.DateTimeFormat(locale).resolvedOptions().calendar
          metrics.device.local_time.calendar_system = calendar || 'gregorian'
        } catch (error) {
          metrics.device.local_time.calendar_system = 'gregorian'
        }
      }
    } catch (error) {
      console.warn('Advanced timezone detection failed:', error)
    }

    // Daylight Saving Time detection
    try {
      metrics.device.local_time.is_dst = this.isDaylightSavingTime(now)
      metrics.device.local_time.dst_offset = this.getDSTOffset(now)
    } catch (error) {
      console.warn('DST detection failed:', error)
    }

    // Session timing
    if (!this.sessionStartTime) {
      this.sessionStartTime = now
    }
    metrics.device.local_time.session_start_local_time = this.sessionStartTime.toISOString()
    metrics.device.local_time.session_start_utc_time = new Date(
      this.sessionStartTime.getTime()
    ).toISOString()

    // Time accuracy estimation
    metrics.device.local_time.time_accuracy = this.estimateTimeAccuracy()

    // Additional timing information
    metrics.device.local_time.week_of_year = this.getWeekOfYear(now)
    metrics.device.local_time.day_of_year = this.getDayOfYear(now)
    metrics.device.local_time.quarter = Math.floor((now.getMonth() + 3) / 3)

    // Relative time information
    metrics.device.local_time.relative_times = {
      start_of_day: new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString(),
      start_of_week: this.getStartOfWeek(now).toISOString(),
      start_of_month: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      start_of_year: new Date(now.getFullYear(), 0, 1).toISOString()
    }
  }

  // Helper methods for time detection
  isDaylightSavingTime(date) {
    const january = new Date(date.getFullYear(), 0, 1)
    const july = new Date(date.getFullYear(), 6, 1)
    const stdTimezoneOffset = Math.max(january.getTimezoneOffset(), july.getTimezoneOffset())
    return date.getTimezoneOffset() < stdTimezoneOffset
  }

  getDSTOffset(date) {
    const january = new Date(date.getFullYear(), 0, 1)
    const july = new Date(date.getFullYear(), 6, 1)
    const stdTimezoneOffset = Math.max(january.getTimezoneOffset(), july.getTimezoneOffset())
    return stdTimezoneOffset - date.getTimezoneOffset()
  }

  getHighResTimestamp() {
    if (performance && performance.timeOrigin) {
      return performance.timeOrigin + performance.now()
    }
    return Date.now()
  }

  estimateTimeAccuracy() {
    // Simple time accuracy estimation by measuring multiple time calls
    const samples = []
    for (let i = 0; i < 10; i++) {
      const start = performance.now()
      Date.now()
      const end = performance.now()
      samples.push(end - start)
    }

    const avgAccuracy = samples.reduce((a, b) => a + b, 0) / samples.length
    return Math.round(avgAccuracy * 1000) / 1000 // Round to 3 decimal places
  }

  getWeekOfYear(date) {
    const startOfYear = new Date(date.getFullYear(), 0, 1)
    const pastDaysOfYear = (date - startOfYear) / 86400000
    return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7)
  }

  getDayOfYear(date) {
    const startOfYear = new Date(date.getFullYear(), 0, 0)
    const diff = date - startOfYear
    return Math.floor(diff / 86400000)
  }

  getStartOfWeek(date) {
    const day = date.getDay()
    const diff = date.getDate() - day + (day === 0 ? -6 : 1) // Adjust for Monday start
    return new Date(date.setDate(diff))
  }

  collectDeviceInfo_old(metrics) {
    const ua = navigator.userAgent

    // Device type detection
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      if (/iPad|Android(?=.*Tablet)|Tablet/i.test(ua)) {
        metrics.device.type = 'tablet'
      } else {
        metrics.device.type = 'mobile'
      }
    } else if (ua.includes('TV') || ua.includes('SmartTV')) {
      metrics.device.type = 'tv'
    } else if (ua.includes('Kiosk') || ua.includes('BroadSign') || ua.includes('Scala')) {
      metrics.device.type = 'kiosk'
    } else {
      metrics.device.type = 'desktop'
    }

    // OS detection
    if (ua.includes('Windows')) {
      metrics.device.os = 'windows'
    } else if (ua.includes('Mac OS')) {
      metrics.device.os = 'macos'
    } else if (ua.includes('Android')) {
      metrics.device.os = 'android'
    } else if (ua.includes('iPhone') || ua.includes('iPad')) {
      metrics.device.os = 'ios'
    } else if (ua.includes('Linux')) {
      metrics.device.os = 'linux'
    }

    // Touch capabilities
    metrics.device.touch_support = 'ontouchstart' in window
    metrics.device.max_touch_points = navigator.maxTouchPoints || 0

    // Screen information
    const screen = window.screen
    const orientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
    metrics.device.screen = {
      width: screen.width,
      height: screen.height,
      available_width: screen.availWidth,
      available_height: screen.availHeight,
      color_depth: screen.colorDepth,
      pixel_ratio: window.devicePixelRatio || 1,
      orientation: screen.orientation ? screen.orientation.type : orientation
    }

    // Viewport
    metrics.device.viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      zoom_level: Math.round((window.outerWidth / window.innerWidth) * 100) / 100
    }
  }

  collectHardwareInfo(metrics) {
    // Memory information
    if (performance.memory) {
      metrics.hardware.memory = {
        total_js_heap: performance.memory.totalJSHeapSize,
        used_js_heap: performance.memory.usedJSHeapSize,
        heap_limit: performance.memory.jsHeapSizeLimit,
        device_memory: navigator.deviceMemory || null
      }
    }

    // CPU information
    metrics.hardware.cpu = {
      cores: navigator.hardwareConcurrency || null,
      estimated_speed: this.benchmarkCPU()
    }

    // GPU information
    const gl = this.getWebGLContext()
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
      metrics.hardware.gpu = {
        vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'unknown',
        renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown',
        webgl_version: gl.getParameter(gl.VERSION),
        max_texture_size: gl.getParameter(gl.MAX_TEXTURE_SIZE),
        supported_extensions: gl.getSupportedExtensions()
      }
    }

    // Media capabilities
    this.detectMediaSupport(metrics)
  }

  detectFeatures(metrics) {
    // ✅ SAFE FEATURE DETECTION - No permissions required
    metrics.features = {
      service_worker: 'serviceWorker' in navigator,
      web_workers: typeof Worker !== 'undefined',
      shared_array_buffer: typeof SharedArrayBuffer !== 'undefined',
      webgl: !!this.getWebGLContext(),
      webgl2: !!this.getWebGL2Context(),
      web_audio: 'AudioContext' in window || 'webkitAudioContext' in window,
      fullscreen_api: !!(document.fullscreenEnabled || document.webkitFullscreenEnabled),
      local_storage: this.testLocalStorage(),
      session_storage: this.testSessionStorage(),
      indexed_db: 'indexedDB' in window
    }
  }

  // Helper methods
  testLocalStorage() {
    try {
      localStorage.setItem('test', 'test')
      localStorage.removeItem('test')
      return true
    } catch {
      return false
    }
  }

  testSessionStorage() {
    try {
      sessionStorage.setItem('test', 'test')
      sessionStorage.removeItem('test')
      return true
    } catch {
      return false
    }
  }

  getWebGLContext() {
    const canvas = document.createElement('canvas')
    return canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
  }

  getWebGL2Context() {
    const canvas = document.createElement('canvas')
    return canvas.getContext('webgl2')
  }

  benchmarkCPU() {
    // Simple CPU benchmark
    const start = performance.now()
    let result = 0
    for (let i = 0; i < 100000; i++) {
      result += Math.random()
    }
    const end = performance.now()
    return Math.round(100000 / (end - start)) // Operations per ms
  }

  detectMediaSupport(metrics) {
    // ✅ SAFE MEDIA DETECTION - No permissions required
    const video = document.createElement('video')
    const audio = document.createElement('audio')

    metrics.hardware.media_support = {
      video_codecs: {
        h264: video.canPlayType('video/mp4; codecs="avc1.42E01E"') !== '',
        vp8: video.canPlayType('video/webm; codecs="vp8"') !== '',
        vp9: video.canPlayType('video/webm; codecs="vp9"') !== '',
        av1: video.canPlayType('video/mp4; codecs="av01.0.08M.08"') !== ''
      },
      audio_codecs: {
        aac: audio.canPlayType('audio/mp4; codecs="mp4a.40.2"') !== '',
        opus: audio.canPlayType('audio/webm; codecs="opus"') !== '',
        vorbis: audio.canPlayType('audio/ogg; codecs="vorbis"') !== '',
        mp3: audio.canPlayType('audio/mpeg') !== ''
      },
      hdr_support:
        video.canPlayType('video/mp4; codecs="hev1.2.4.L153.B0"; eotf="smpte2084"') !== ''

      // ❌ REMOVED - DRM detection can trigger permission prompts:
      // drm_support: {
      //   widevine: 'requestMediaKeySystemAccess' in navigator,
      //   playready: 'MSMediaKeys' in window,
      //   fairplay: 'WebKitMediaKeys' in window
      // }
    }
  }
}

// ============================================================================
// 3. ⚡ PERFORMANCE COLLECTOR
// ============================================================================

export class PerformanceCollector {
  constructor() {
    this.performanceObserver = null
    this.resourceTimings = []
    this.longTasks = []
  }

  async collectPerformanceMetrics() {
    const metrics = { ...PerformanceMetrics }

    try {
      // Page load performance
      this.collectPageLoadMetrics(metrics)

      // Resource loading metrics
      this.collectResourceMetrics(metrics)

      // Runtime performance
      await this.collectRuntimeMetrics(metrics)

      metrics.timestamp = new Date().toISOString()
      return metrics
    } catch (error) {
      console.error('Performance metrics collection failed:', error)
      return null
    }
  }

  collectPageLoadMetrics(metrics) {
    const timing = performance.timing
    const navigation = performance.getEntriesByType('navigation')[0]

    if (timing) {
      const navigationStart = timing.navigationStart

      metrics.page_load = {
        navigation_start: 0,
        dns_lookup: timing.domainLookupEnd - timing.domainLookupStart,
        tcp_connect: timing.connectEnd - timing.connectStart,
        ssl_handshake: timing.secureConnectionStart
          ? timing.connectEnd - timing.secureConnectionStart
          : 0,
        request_start: timing.requestStart - navigationStart,
        response_start: timing.responseStart - navigationStart,
        response_end: timing.responseEnd - navigationStart,
        dom_loading: timing.domLoading - navigationStart,
        dom_interactive: timing.domInteractive - navigationStart,
        dom_complete: timing.domComplete - navigationStart,
        load_event_start: timing.loadEventStart - navigationStart,
        load_event_end: timing.loadEventEnd - navigationStart,

        // Derived metrics
        total_load_time: timing.loadEventEnd - navigationStart,
        time_to_first_byte: timing.responseStart - navigationStart,
        time_to_interactive: timing.domInteractive - navigationStart
      }
    }

    // Modern performance metrics
    if (navigation) {
      metrics.page_load.first_contentful_paint = this.getMetricValue('first-contentful-paint')
      metrics.page_load.largest_contentful_paint = this.getMetricValue('largest-contentful-paint')
      metrics.page_load.cumulative_layout_shift = this.getMetricValue('cumulative-layout-shift')
    }
  }

  collectResourceMetrics(metrics) {
    const resources = performance.getEntriesByType('resource')

    metrics.resources = {
      scripts: [],
      stylesheets: [],
      images: [],
      videos: [],
      fonts: [],
      xhr_requests: [],
      total_resources: resources.length,
      total_size_bytes: 0,
      failed_resources: 0,
      cached_resources: 0
    }

    resources.forEach((resource) => {
      const resourceData = {
        url: this.anonymizeUrl(resource.name),
        size: resource.transferSize || 0,
        load_time: resource.responseEnd - resource.startTime,
        cache_hit: resource.transferSize === 0 && resource.decodedBodySize > 0
      }

      if (resourceData.cache_hit) {
        metrics.resources.cached_resources++
      }

      metrics.resources.total_size_bytes += resourceData.size

      // Categorize by type
      if (resource.name.includes('.js')) {
        metrics.resources.scripts.push(resourceData)
      } else if (resource.name.includes('.css')) {
        metrics.resources.stylesheets.push(resourceData)
      } else if (/\.(jpg|jpeg|png|gif|webp|svg)/.test(resource.name)) {
        metrics.resources.images.push(resourceData)
      } else if (/\.(mp4|webm|ogg|avi)/.test(resource.name)) {
        metrics.resources.videos.push(resourceData)
      } else if (/\.(woff|woff2|ttf|otf)/.test(resource.name)) {
        metrics.resources.fonts.push(resourceData)
      } else if (
        resource.initiatorType === 'xmlhttprequest' ||
        resource.initiatorType === 'fetch'
      ) {
        metrics.resources.xhr_requests.push(resourceData)
      }
    })
  }

  collectRuntimeMetrics(metrics) {
    // Pastikan metrics.runtime sudah ada
    if (!metrics.runtime) {
      metrics.runtime = {}
    }
    // Memory snapshots
    if (performance.memory) {
      metrics.runtime.memory_snapshots = [
        {
          timestamp: Date.now(),
          used_heap: performance.memory.usedJSHeapSize,
          total_heap: performance.memory.totalJSHeapSize
        }
      ]
    }
    if (!metrics.performance) {
      metrics.performance = {}
    }
    if (!metrics.performance.memory_snapshots) {
      metrics.performance.memory_snapshots = []
    }

    // Long tasks
    metrics.runtime.long_tasks = this.longTasks.slice(-20) // Last 20 long tasks

    // FPS measurement
    this.measureFrameRate(metrics)
  }

  measureFrameRate(metrics) {
    return new Promise((resolve) => {
      let frames = 0
      let startTime = performance.now()

      const measureFrame = () => {
        frames++
        if (frames < 60) {
          // Measure for ~1 second
          requestAnimationFrame(measureFrame)
        } else {
          const endTime = performance.now()
          const duration = endTime - startTime
          const fps = Math.round((frames * 1000) / duration)

          if (!metrics.runtime) {
            metrics.runtime = {}
          }
          metrics.runtime.frame_rate = {
            average_fps: fps,
            min_fps: fps, // Simplified for this example
            max_fps: fps,
            frame_drops: 0,
            long_tasks: this.longTasks.length
          }

          resolve()
        }
      }

      requestAnimationFrame(measureFrame)
    })
  }

  getMetricValue(metricName) {
    const entries = performance.getEntriesByName(metricName)
    return entries.length > 0 ? entries[0].value : null
  }

  anonymizeUrl(url) {
    // Remove query parameters and sensitive information
    try {
      const urlObj = new URL(url)
      return urlObj.pathname
    } catch {
      return url.split('?')[0]
    }
  }

  startPerformanceObservation() {
    if ('PerformanceObserver' in window) {
      // Observe long tasks
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            this.longTasks.push({
              duration: entry.duration,
              start_time: entry.startTime,
              name: entry.name
            })
          })
        })
        longTaskObserver.observe({ entryTypes: ['longtask'] })
      } catch (error) {
        console.warn('Long task observation not supported:', error)
      }
    }
  }
}

// ============================================================================
// 4. 🎬 CONTENT METRICS COLLECTOR
// ============================================================================

export class ContentMetricsCollector {
  constructor() {
    this.contentStartTime = null
    this.videoEvents = []
    this.imageEvents = []
    this.interactionEvents = []
  }

  collectContentMetrics(contentType, contentUrl) {
    const metrics = { ...ContentMetrics }

    metrics.content_load = {
      request_start: this.contentStartTime,
      first_byte_received: null,
      content_ready: Date.now(),
      total_load_time: this.contentStartTime ? Date.now() - this.contentStartTime : null,
      content_size_bytes: null,
      content_type: contentType,
      content_url: this.anonymizeUrl(contentUrl),
      cdn_used: this.detectCDN(contentUrl),
      cache_status: 'unknown'
    }

    if (contentType === 'video') {
      this.collectVideoMetrics(metrics)
    } else if (contentType === 'image') {
      this.collectImageMetrics(metrics)
    }

    this.collectInteractionMetrics(metrics)

    metrics.timestamp = new Date().toISOString()
    return metrics
  }

  collectVideoMetrics(metrics) {
    const videoElement =
      document.getElementById('video-content') || document.getElementById('mediaElement')

    if (videoElement) {
      metrics.video_playback = {
        duration_total: videoElement.duration || null,
        duration_played: this.calculatePlayedDuration(videoElement),
        completion_rate: this.calculateCompletionRate(videoElement),

        video_codec: this.detectVideoCodec(videoElement),
        audio_codec: this.detectAudioCodec(videoElement),

        play_attempts: this.videoEvents.filter((e) => e.type === 'play').length,
        successful_plays: this.videoEvents.filter((e) => e.type === 'playing').length,
        pause_events: this.videoEvents.filter((e) => e.type === 'pause'),

        buffering_events: this.videoEvents.filter((e) => e.type === 'waiting'),
        error_events: this.videoEvents.filter((e) => e.type === 'error'),

        vast_metrics: this.collectVASTMetrics()
      }
    }
  }

  collectImageMetrics(metrics) {
    const imageElement = document.getElementById('image-content')

    if (imageElement) {
      metrics.image_display = {
        load_time: this.imageEvents.find((e) => e.type === 'load')?.duration || null,
        format: this.detectImageFormat(imageElement.src),
        size_bytes: null, // Would need to be measured during load
        dimensions: {
          original: { width: imageElement.naturalWidth, height: imageElement.naturalHeight },
          displayed: { width: imageElement.width, height: imageElement.height }
        },
        load_errors: this.imageEvents.filter((e) => e.type === 'error'),
        visibility_events: this.imageEvents.filter((e) => e.type === 'intersect')
      }
    }
  }

  collectInteractionMetrics(metrics) {
    metrics.interaction = {
      clicks: this.interactionEvents.filter((e) => e.type === 'click'),
      hovers: this.interactionEvents.filter((e) => e.type === 'mouseover'),
      focus_events: this.interactionEvents.filter((e) => e.type === 'focus'),

      time_in_view: this.calculateTimeInView(),
      interaction_time: this.calculateInteractionTime(),
      attention_score: this.calculateAttentionScore()
    }
  }

  // Helper methods
  anonymizeUrl(url) {
    if (!url) {
      return null
    }
    try {
      const urlObj = new URL(url)
      return urlObj.pathname
    } catch {
      return url.split('?')[0]
    }
  }

  detectCDN(url) {
    if (!url) {
      return null
    }

    const cdnPatterns = [
      'cloudfront.net',
      'cloudflare.com',
      'fastly.com',
      'jsdelivr.net',
      'unpkg.com'
    ]

    return cdnPatterns.find((pattern) => url.includes(pattern)) || 'unknown'
  }

  calculatePlayedDuration(videoElement) {
    // This would require tracking play/pause events over time
    return videoElement.currentTime || 0
  }

  calculateCompletionRate(videoElement) {
    if (!videoElement.duration || videoElement.duration === 0) {
      return 0
    }
    return Math.round((videoElement.currentTime / videoElement.duration) * 100)
  }

  detectVideoCodec(videoElement) {
    // Simplified codec detection
    const src = videoElement.src || videoElement.currentSrc
    if (src && src.includes('.mp4')) {
      return 'h264'
    }
    if (src && src.includes('.webm')) {
      return 'vp8'
    }
    return 'unknown'
  }

  detectAudioCodec(videoElement) {
    const src = videoElement.src || videoElement.currentSrc
    if (src && src.includes('.mp4')) {
      return 'aac'
    }
    if (src && src.includes('.webm')) {
      return 'opus'
    }
    return 'unknown'
  }

  detectImageFormat(src) {
    if (!src) {
      return null
    }
    if (src.includes('.jpg') || src.includes('.jpeg')) {
      return 'jpeg'
    }
    if (src.includes('.png')) {
      return 'png'
    }
    if (src.includes('.webp')) {
      return 'webp'
    }
    if (src.includes('.gif')) {
      return 'gif'
    }
    if (src.includes('.svg')) {
      return 'svg'
    }
    return 'unknown'
  }

  collectVASTMetrics() {
    // This would integrate with your VAST tracking system
    return {
      creative_loaded: true,
      companion_ads: [],
      tracking_events: {},
      quartile_progress: {}
    }
  }

  calculateTimeInView() {
    // Calculate based on intersection observer events
    return this.interactionEvents
      .filter((e) => e.type === 'intersect' && e.isVisible)
      .reduce((total, event) => total + (event.duration || 0), 0)
  }

  calculateInteractionTime() {
    return (
      this.interactionEvents.filter((e) => ['click', 'mouseover', 'focus'].includes(e.type))
        .length * 1000
    ) // Simplified calculation
  }

  calculateAttentionScore() {
    const interactions = this.interactionEvents.length
    const timeInView = this.calculateTimeInView()

    if (timeInView === 0) {
      return 0
    }

    // Simple attention score formula
    return Math.min(100, Math.round((interactions / (timeInView / 1000)) * 100))
  }

  // Event tracking methods
  startContentTracking() {
    this.contentStartTime = Date.now()

    // Video event listeners
    const videoElements = [
      document.getElementById('video-content'),
      document.getElementById('mediaElement')
    ].filter(Boolean)

    videoElements.forEach((video) => {
      ;['play', 'pause', 'ended', 'error', 'waiting', 'playing'].forEach((eventType) => {
        video.addEventListener(eventType, (event) => {
          this.videoEvents.push({
            type: eventType,
            timestamp: Date.now(),
            currentTime: video.currentTime,
            duration: video.duration
          })
        })
      })
    })

    // Image event listeners
    const imageElement = document.getElementById('image-content')
    if (imageElement) {
      ;['load', 'error'].forEach((eventType) => {
        imageElement.addEventListener(eventType, (event) => {
          this.imageEvents.push({
            type: eventType,
            timestamp: Date.now(),
            duration: Date.now() - this.contentStartTime
          })
        })
      })
    }

    // Interaction event listeners
    document.addEventListener('click', this.trackInteraction.bind(this))
    document.addEventListener('mouseover', this.trackInteraction.bind(this))
    document.addEventListener('focus', this.trackInteraction.bind(this))
  }

  trackInteraction(event) {
    this.interactionEvents.push({
      type: event.type,
      timestamp: Date.now(),
      element: event.target.tagName,
      coordinates: {
        x: event.clientX || 0,
        y: event.clientY || 0
      }
    })
  }
}

// ============================================================================
// 5. 🎯 MASTER COLLECTOR CLASS
// ============================================================================

export class MasterDataCollector {
  constructor() {
    this.networkCollector = new NetworkPerformanceCollector()
    this.browserCollector = new BrowserHardwareCollector()
    this.performanceCollector = new PerformanceCollector()
    this.contentCollector = new ContentMetricsCollector()

    this.sessionId = this.generateSessionId()
    this.isInitialized = false
  }

  async initialize() {
    if (this.isInitialized) {
      return
    }

    try {
      // Start monitoring systems
      await this.networkCollector.startConnectionMonitoring()
      await this.performanceCollector.startPerformanceObservation()
      await this.contentCollector.startContentTracking()

      this.isInitialized = true
      console.log('📊 Master Data Collector initialized')
    } catch (error) {
      console.error('Failed to initialize data collector:', error)
    }
  }

  async collectAllMetrics(eventType = 'general') {
    if (!this.isInitialized) {
      await this.initialize()
    }

    try {
      const masterLog = {}

      // Collect all metrics in parallel
      const [networkMetrics, browserMetrics, performanceMetrics] = await Promise.all([
        this.networkCollector.collectNetworkMetrics(),
        this.browserCollector.collectBrowserHardwareMetrics(),
        this.performanceCollector.collectPerformanceMetrics()
      ])

      // Content metrics (synchronous)
      const contentMetrics = this.contentCollector.collectContentMetrics(
        'video',
        window.location.href
      )

      // Assemble master log entry
      masterLog.event_type = eventType
      masterLog.session_id = this.sessionId
      masterLog.timestamp = new Date().toISOString()

      masterLog.network = networkMetrics
      masterLog.browser_hardware = browserMetrics
      masterLog.performance = performanceMetrics
      masterLog.content = contentMetrics

      // Context information
      masterLog.context = {
        url: window.location.pathname,
        route: this.detectRoute(),
        user_agent: navigator.userAgent,
        deployment_env: this.detectEnvironment(),
        features_enabled: this.getActiveFeatures(),
        priority: this.determinePriority(eventType),
        sample_rate: this.getSampleRate()
      }

      return masterLog
    } catch (error) {
      console.error('Failed to collect metrics:', error)
      return null
    }
  }

  // Utility methods
  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  detectRoute() {
    const path = window.location.pathname
    if (path.includes('/creative/player/broadsign')) {
      return 'broadsign'
    }
    if (path.includes('/creative/player/basic')) {
      return 'basic'
    }
    if (path.includes('/creative/player')) {
      return 'player'
    }
    return 'unknown'
  }

  detectEnvironment() {
    const hostname = window.location.hostname
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return 'dev'
    }
    if (hostname.includes('staging')) {
      return 'staging'
    }
    return 'prod'
  }

  getActiveFeatures() {
    // Return list of active features/flags
    return []
  }

  determinePriority(eventType) {
    if (eventType.includes('error')) {
      return 'critical'
    }
    if (eventType.includes('performance')) {
      return 'high'
    }
    return 'normal'
  }

  getSampleRate() {
    // Implement sampling logic
    return 1.0 // 100% for now
  }
}

// Export main collector
export default MasterDataCollector
